// InventoryList.tsx
import React, { useEffect } from "react";
import { DataTable } from "../../../DataTable/DataTable";
import { Ban, CircleCheck, Edit, Trash } from "lucide-react";
import toast from "react-hot-toast";
import { InventoryItem, InvoiceItem } from "./types";
import UpdateSellingPriceModal from "../../../../shared/UpdateSellingPriceModal";
import { InverntroyService } from "../../../../services/api/inventroy.service";

interface InventoryListProps {
  searchTerm?: string;
  filter?: any;
  setShowSpModal: any;
  showSpModal: boolean;
}

const InventoryList: React.FC<InventoryListProps> = ({
  searchTerm,
  filter,
  setShowSpModal,
  showSpModal,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalItemsCount, setTotalItemsCount] = React.useState(0);
  const [inventoryData, setInventoryData] = React.useState<InventoryItem[]>([]);
  const [showModal, setShowModal] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<InvoiceItem | null>(
    null
  );

  const loadInventoryData = async (
    page: number,
    pageSize: number,
    searchTerm?: string,
    filter?: any
  ) => {
    try {
      setIsLoading(true);
      const response = await InverntroyService.getInventryList(
        page,
        pageSize,
        searchTerm,
        filter
      );
      if (response.success) {
        setInventoryData(response.data);
      } else {
        setInventoryData([]);
      }
      setTotalItemsCount(response.pagination.totalItemsCount || 0);
      setTotalPages(response.pagination.totalPages || 1);
    } catch (error) {
      toast.error(`${error}`);
      setInventoryData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInvoiceData = async (invoiceNumber: string, id: string) => {
    try {
      setIsLoading(true);
      const response = await InverntroyService.getInvoiceHistoryDetails(
        invoiceNumber
      );

      if (response.data.length > 0) {
        const matchedItem = response.data.find((item: any) => item.id === id);

        if (matchedItem) {
          setSelectedItem(matchedItem);
        } else {
          toast.error("No data found matching the provided ID");
          setSelectedItem(null); // Or use [] if selectedItem is expected to be an array
        }
      } else {
        toast.error(response.message || "No invoice data found");
        setSelectedItem(null);
      }
    } catch (error) {
      console.error("Error loading invoice data:", error);
      toast.error("Something went wrong while loading invoice data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSellingPrice = async (item: InventoryItem) => {
    try {
      await loadInvoiceData(item.invoice_number, item.id);
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      setShowSpModal(true);
    }
  };

  useEffect(() => {
    loadInventoryData(currentPage, pageSize, searchTerm, filter);
  }, [currentPage, pageSize, searchTerm, filter]);

  const columns = [
    {
      key: "productName",
      title: "Product Name",
      width: "w-[200px]",
      render: (item: InventoryItem) => (
        <div
          className="text-sm font-medium line-clamp-2 cursor-default"
          title={item?.product?.name}
        >
          {item?.product?.name || "N/A"}
        </div>
      ),
    },
    {
      key: "manufacturerName",
      title: "Manufacturer Name",
      render: (item: InventoryItem) => (
        <div>{item?.manufacturer?.manufacturer_name || "N/A"}</div>
      ),
    },
    {
      key: "marketerName",
      title: "Marketer Name",
      render: (item: InventoryItem) => (
        <div>{item?.marketer?.marketer_name || "N/A"}</div>
      ),
    },
    {
      key: "clinicName",
      title: "Clinic Name",
      width: "w-[200px]",
      render: (item: InventoryItem) => (
        <div className="flex items-center space-x-2">
          {/* <img
            src={item?.clinic?.profileImage || ""}
            alt={item?.clinic?.name}
            className="w-8 h-8 rounded"
          /> */}
          <div
            className="text-sm font-medium line-clamp-2 cursor-default"
            title={item?.clinic?.name}
          >
            {item?.clinic?.name || "N/A"}
          </div>
        </div>
      ),
    },
    {
      key: "expiryDate",
      title: "Expiry Date",
      render: (item: InventoryItem) => (
        <div>
          {item.expiry_date
            ? (() => {
                const date = new Date(item.expiry_date);
                const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
                const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year
                return `${month}/${year}`;
              })()
            : "N/A"}
        </div>
      ),
    },
    {
      key: "quantity",
      title: "Quantity",
      render: (item: InventoryItem) => <div>{item?.no_of_items || "N/A"}</div>,
    },
    
    {
      key: "mrp",
      title: "MRP",
      render: (item: InventoryItem) => <div>{Number(item?.mrp).toFixed(2) || "N/A"}</div>,
    },
    {
      key: "newMRP",
      title: "New MRP",
      render: (item: InventoryItem) => <div>{Number(item?.productPrice?.new_mrp || item?.mrp).toFixed(2) || "N/A"}</div>,
    },
    {
      key: "productRate",
      title: "Product Rate",
      render: (item: InventoryItem) => <div>{Number(item?.product_rate).toFixed(2) || "N/A"}</div>,
    },
    {
      key: "LandingCost",
      title: "Landing Cost",
      render: (item: InventoryItem) => (
        <div>{Number(item?.landing_cost).toFixed(2) || "N/A"}</div>
      ),
    },
    {
      key: "updateSP",
      title: "Updated SP",
      render: (item: InventoryItem) => (
        <div>
          {item?.productPrice
            ? Number(item?.productPrice?.final_selling_price).toFixed(2)
            : "N/A"}
        </div>
      ),
    },
    {
      key: "isSPUpdated",
      title: "SP Updated?",
      render: (item: InventoryItem) => (
        <div>
          {item?.is_sp_updated === true ? (
            <CircleCheck className="w-5 h-5 bg-[#199C4D] rounded-full text-[white]" />
          ) : item?.is_sp_updated === false ? (
            <Ban className="w-5 h-5 bg-[#c62a2a] rounded-full text-[white]" />
          ) : (
            "N/A"
          )}
        </div>
      ),
    },
    {
      key: "Actions",
      title: "Selling Price",
      width: "w-[150px]",
      render: (item: InventoryItem) => (
        <div className="flex space-x-4">
          <button
            onClick={() => handleUpdateSellingPrice(item)}
            className="px-3 py-2 text-sm text-green-600 border border-[#199C4D] rounded-md transition-colors font-medium"
          >
            Update
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {showSpModal && selectedItem ? (
        <UpdateSellingPriceModal
          id={selectedItem.id}
          productID={selectedItem.product_id}
          onClose={() => {
            setShowSpModal(false);
            loadInventoryData(currentPage, pageSize, searchTerm, filter);
          }}
        />
      ) : (
        <DataTable
          columns={columns}
          data={inventoryData}
          isLoading={isLoading}
          pagination={{
            currentPage,
            setCurrentPage,
            setTotalPages,
            totalPages,
            totalItemsCount,
            pageSize,
            setPageSize,
          }}
        />
      )}
    </div>
  );
};

export default InventoryList;
